import { Fragment } from 'react';
import { View, Text, Svg, Path } from '@react-pdf/renderer';
import type {
  RepositoryAnalysis,
  Recommendation,
  Finding,
} from '../../../components/types/analysis.types';
import {
  buildSummaryParts,
  connectorKind,
  derivePipelineStages,
  deriveStageTools,
  gapPrioritySummary,
  isLowFrictionHighROI,
  languagesLabel,
  primaryLanguage,
  recPrioritySummary,
  recType,
  countTechnologies,
  technologyLabel,
  formatDuration,
  PIPELINE_STAGE_ORDER,
} from './derive';
import {
  styles as s,
  colors,
  scoreColor,
  severityColors,
  recTypeColors,
  connectorColor,
} from './styles';

function muted(text: string): JSX.Element {
  return <Text style={s.gapDesc}>{text}</Text>;
}

// Drawn (not glyph-based) so the mark centers deterministically and doesn't
// depend on the report font carrying U+2713 / U+2717.
function StageMark({ covered }: { covered: boolean }): JSX.Element {
  const color = covered ? colors.greenInk : colors.red;
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24">
      <Path
        d={covered ? 'M20 6L9 17l-5-5' : 'M6 6l12 12M18 6L6 18'}
        stroke={color}
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </Svg>
  );
}

export function MetricsRow({ data }: { data: RepositoryAnalysis }): JSX.Element {
  const lang = languagesLabel(data) || primaryLanguage(data);
  const duration = formatDuration(data.scanDuration);
  const gapItems = (data.gaps ?? []).filter((g) => g.kind !== 'issue');
  const recs = data.recommendations ?? [];
  const filesSub = [data.repositoryDetails?.projectType, lang, duration ? `${duration} scan` : '']
    .filter(Boolean)
    .join(' · ');

  const cards: Array<{ value: string; valueColor?: string; label: string; sub: string }> = [
    { value: `${data.filesCount ?? '—'}`, label: 'Files Analyzed', sub: filesSub },
    {
      value: `${countTechnologies(data)}`,
      label: 'Technologies Detected',
      sub: technologyLabel(data),
    },
    {
      value: `${gapItems.length}`,
      valueColor: gapItems.length > 0 ? colors.red : colors.green,
      label: 'Gaps Identified',
      sub: gapPrioritySummary(gapItems),
    },
    {
      value: `${recs.length}`,
      valueColor: colors.teal,
      label: 'Recommendations',
      sub: recPrioritySummary(recs),
    },
  ];

  return (
    <View style={s.metricsRow}>
      {cards.map((c, i) => (
        <View key={i} style={s.metricCell}>
          <View style={s.metricCard}>
            <Text style={[s.metricValue, c.valueColor ? { color: c.valueColor } : {}]}>
              {c.value}
            </Text>
            <Text style={s.metricLabel}>{c.label}</Text>
            {c.sub ? <Text style={s.metricSub}>{c.sub}</Text> : null}
          </View>
        </View>
      ))}
    </View>
  );
}

export function SummaryText({ data }: { data: RepositoryAnalysis }): JSX.Element {
  const p = buildSummaryParts(data);
  return (
    <View style={[s.glass, s.summaryCard]}>
      <Text style={s.summaryText}>
        <Text style={s.bold}>{p.repoName}</Text>
        {p.afterName}
        <Text style={s.bold}>{p.scoreText}</Text>
        {p.afterScore}
      </Text>
    </View>
  );
}

export function PipelineSection({ data }: { data: RepositoryAnalysis }): JSX.Element {
  const stages = derivePipelineStages(data);
  return (
    <View style={[s.glass, s.pipelineCard]} wrap={false}>
      <View style={s.pipelineTrack}>
        {stages.map((stage, i) => (
          <Fragment key={stage.name}>
            <View style={s.pipelineCol}>
              <View style={[s.node, stage.covered ? s.nodeCovered : s.nodeGap]}>
                <StageMark covered={stage.covered} />
              </View>
              <Text style={[s.stageName, stage.covered ? {} : s.stageNameGap]}>{stage.name}</Text>
            </View>
            {i < stages.length - 1 ? (
              <View style={s.connector}>
                <View
                  style={[
                    s.connectorLine,
                    { backgroundColor: connectorColor(connectorKind(stage, stages[i + 1])) },
                  ]}
                />
              </View>
            ) : null}
          </Fragment>
        ))}
      </View>
    </View>
  );
}

export function CategoryTable({ data }: { data: RepositoryAnalysis }): JSX.Element | null {
  if (!data.categories?.length) return null;
  return (
    <View style={s.catTable}>
      {data.categories.map((cat, i) => {
        const notScored = cat.scored === false;
        const color = scoreColor(cat.score);
        const pct = Math.min(cat.score, 100);
        const bd = cat.breakdown;
        return (
          <View key={cat.id} style={[s.catCell, i % 2 === 0 ? s.catCellPadRight : {}]}>
            <View style={s.catRow} wrap={false}>
              <Text style={s.catName}>{cat.name}</Text>
              <View style={s.catBarBg}>
                {!notScored ? (
                  <View
                    style={[s.catBarFill, { width: (pct / 100) * 56, backgroundColor: color }]}
                  />
                ) : null}
              </View>
              <Text style={[s.catScore, { color: notScored ? '#94a3b8' : color }]}>
                {notScored ? '—' : cat.score}
              </Text>
              {bd && !notScored ? (
                <Text style={s.catBreakdown}>
                  {`Detection ${bd.detection}/50 · Quality ${bd.quality}/50 · ${bd.covered.length}/${bd.expected.length} expected tool areas covered`}
                </Text>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
}

function SeverityBadge({ severity }: { severity?: string }): JSX.Element {
  return <Text style={[s.sevBadge, severityColors(severity)]}>{severity ?? 'low'}</Text>;
}

export function GapsList({ gaps }: { gaps: Finding[] }): JSX.Element {
  const onlyGaps = gaps.filter((g) => g.kind !== 'issue');
  if (!onlyGaps.length) return muted('No gaps identified.');
  return (
    <View>
      {onlyGaps.map((gap) => (
        <View key={gap.id} style={s.gapItem} wrap={false}>
          <View style={s.gapHeader}>
            <SeverityBadge severity={gap.severity} />
            <Text style={s.gapTitle}>{gap.title}</Text>
          </View>
          {gap.description ? <Text style={s.gapDesc}>{gap.description}</Text> : null}
        </View>
      ))}
    </View>
  );
}

export function StrengthsList({ strengths }: { strengths: Finding[] }): JSX.Element {
  if (!strengths?.length) return muted('No strengths identified.');
  return (
    <View>
      {strengths.map((item) => {
        const desc = item.description && item.description !== item.title ? item.description : null;
        return (
          <View key={item.id} style={s.strengthItem} wrap={false}>
            <Text style={s.strengthIcon}>{'✓'}</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.strengthTitle}>{item.title}</Text>
              {desc ? <Text style={s.strengthDesc}>{desc}</Text> : null}
            </View>
          </View>
        );
      })}
    </View>
  );
}

export function RecommendationsGrid({
  recs,
  quickWinIds,
}: {
  recs: Recommendation[];
  quickWinIds: Set<string>;
}): JSX.Element | null {
  if (!recs?.length) return null;
  return (
    <View style={s.recGrid}>
      {recs.map((rec, i) => {
        const rt = recType(rec.title);
        const lfhr = isLowFrictionHighROI(rec) || quickWinIds.has(rec.id);
        return (
          <View key={rec.id} style={[s.recCell, i % 2 === 0 ? { paddingRight: 8 } : {}]}>
            <View style={s.recItem} wrap={false}>
              <Text style={s.recNumber}>{i + 1}</Text>
              <View style={s.recContent}>
                <View style={s.recHeader}>
                  <SeverityBadge severity={rec.priority} />
                  {rt ? (
                    <Text style={[s.typeBadge, recTypeColors(rt.kind)]}>{rt.label}</Text>
                  ) : null}
                  {lfhr ? <Text style={s.lfhrBadge}>Low Friction, High ROI</Text> : null}
                  <Text style={s.recTitle}>{rec.title}</Text>
                </View>
                {rec.description ? <Text style={s.recReason}>{rec.description}</Text> : null}
                {rec.estimatedTime ? <Text style={s.recMeta}>{rec.estimatedTime}</Text> : null}
                {(rec.steps ?? []).map((step, j) => (
                  <View key={j} style={s.stepRow}>
                    <Text style={s.stepDot}>{'•'}</Text>
                    <Text style={s.stepText}>{step}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );
}

export function Appendix({ data }: { data: RepositoryAnalysis }): JSX.Element {
  const stageTools = deriveStageTools(data);
  return (
    <View>
      <Text style={s.appendixTitle}>Appendix: Detected Tools Inventory</Text>
      <Text style={s.appendixNote}>
        Complete inventory of detected development tools, grouped by pipeline stage.
      </Text>
      {PIPELINE_STAGE_ORDER.map((stage) => {
        const tools = stageTools[stage] ?? [];
        return (
          <View key={stage} style={s.toolsStageGroup} wrap={false}>
            <Text style={s.toolsStageTitle}>{stage}</Text>
            {tools.length === 0 ? (
              <Text style={s.toolsEmpty}>No {stage.toLowerCase()} tools detected</Text>
            ) : (
              <View style={s.toolsGrid}>
                {tools.map((t, i) => (
                  <View key={`${t.name}-${i}`} style={s.toolCell}>
                    <View style={s.toolCard}>
                      <Text style={s.toolName}>{t.name}</Text>
                      <Text style={s.toolType}>{t.type}</Text>
                      <Text style={s.tierBadge}>Detected</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}
